// src/pages/messages/CustomerMessagesDetailPage.tsx

import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Copy,
  Loader2,
  Mail,
  MessageCircle,
  Phone,
  RefreshCw,
  Router,
  Send,
  Smartphone,
  User,
  Wallet,
  Wifi,
  X,
  XCircle,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../components/ui/Toast";
import { fetchApp } from "../../services/apiService";

interface CustomerInfo {
  customer_id?: string | null;
  secret_id?: string | null;
  name?: string | null;
  username?: string | null;
  phone_number?: string | null;
  email?: string | null;
  account_number?: string | null;
  plan_name?: string | null;
  router_name?: string | null;
  status?: boolean;
  service_expires_at?: string | null;
}

interface CustomerMessage {
  id: string;
  customer_id?: string | null;
  phone_number?: string | null;
  channel: "sms" | "whatsapp" | string;
  message: string;
  delivered: boolean;
  status?: string | null;
  cost?: number | null;
  currency?: string | null;
  provider_message_id?: string | null;
  sent_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface CustomerMessagesResponse {
  customer: CustomerInfo;
  summary: {
    total: number;
    delivered: number;
    failed: number;
    sms: number;
    whatsapp: number;
  };
  items: CustomerMessage[];
  limit: number;
  offset: number;
}

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatMoney = (cost?: number | null, currency?: string | null) => {
  if (cost == null) return "—";

  return `${currency || "KES"} ${Number(cost).toLocaleString("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getInitials = (name?: string | null) =>
  String(name || "Customer")
    .split(" ")
    .filter(Boolean)
    .map((item) => item[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "C";

const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
    <div className="mx-auto flex min-h-[60vh] max-w-[1600px] items-center justify-center">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

        <div className="relative overflow-hidden px-6 py-10 text-center">
          <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-emerald-200/35 blur-2xl" />
          <div className="absolute -bottom-20 left-4 h-52 w-52 rounded-full bg-cyan-200/35 blur-2xl" />

          <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/50">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>

          <p className="relative mt-4 text-[15px] font-black text-slate-900">
            Loading customer messages…
          </p>

          <p className="relative mt-1 text-[12.5px] font-medium text-slate-500">
            Please wait while the message history is being prepared.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ delivered: boolean; status?: string | null }> = ({
  delivered,
  status,
}) => {
  if (delivered) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-[11px] font-black text-emerald-800">
        <CheckCircle2 className="h-3 w-3" />
        {status || "Sent"}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-100 px-2.5 py-1 text-[11px] font-black text-rose-700">
      <XCircle className="h-3 w-3" />
      {status || "Failed"}
    </span>
  );
};

const ChannelBadge: React.FC<{ channel: string }> = ({ channel }) => {
  const isWhatsapp = channel === "whatsapp";

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-black ${
        isWhatsapp
          ? "border-emerald-200 bg-emerald-100 text-emerald-800"
          : "border-sky-200 bg-sky-100 text-sky-800"
      }`}
    >
      {isWhatsapp ? (
        <MessageCircle className="h-3 w-3" />
      ) : (
        <Smartphone className="h-3 w-3" />
      )}
      {isWhatsapp ? "WhatsApp" : "SMS"}
    </span>
  );
};

const DetailRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}> = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 rounded-3xl border border-slate-200 bg-slate-100 px-4 py-4">
    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
      {icon}
    </div>

    <div className="min-w-0">
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
        {label}
      </p>

      <p className="mt-1 break-words text-[13px] font-black text-slate-900">
        {value}
      </p>
    </div>
  </div>
);

const MiniInfo: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
      {label}
    </p>

    <p className="mt-1 break-words text-[12px] font-bold text-slate-800">
      {value}
    </p>
  </div>
);

const CustomerMessagesDetailPage: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [data, setData] = useState<CustomerMessagesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const customer = data?.customer;
  const messages = data?.items || [];
  const summary = data?.summary;

  const customerName =
    customer?.name ||
    customer?.username ||
    customer?.account_number ||
    customer?.phone_number ||
    "Customer";

  const loadMessages = async (manual = false) => {
    if (!customerId) {
      setError("Missing customer ID.");
      setLoading(false);
      return;
    }

    try {
      if (manual) setRefreshing(true);
      else setLoading(true);

      setError(null);

      const res = await fetchApp<CustomerMessagesResponse>(
        `/messages/customer/${encodeURIComponent(customerId)}?limit=200&offset=0`,
      );

      setData(res);
    } catch (err: any) {
      const msg = err?.message || "Failed to load customer messages.";
      setError(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadMessages(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const latestMessage = useMemo(() => {
    return messages[0] || null;
  }, [messages]);

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast("Copied to clipboard", "success");
    } catch {
      showToast("Failed to copy", "error");
    }
  };

  if (loading && !data) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="mb-3 inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-[12px] font-black text-slate-600 shadow-sm transition hover:bg-slate-100 hover:text-slate-900"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Messages
                </button>

                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Customer Message History
                </div>

                <div className="mt-4 flex items-center gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-3xl bg-emerald-100 text-[16px] font-black text-emerald-700 ring-1 ring-emerald-200">
                    {getInitials(customerName)}
                  </div>

                  <div className="min-w-0">
                    <h1 className="truncate text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                      {customerName}
                    </h1>

                    <p className="mt-1 text-[13px] font-medium text-slate-500">
                      View all SMS and WhatsApp messages for this customer.
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => loadMessages(true)}
                disabled={refreshing}
                className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
              >
                {refreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {refreshing ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {refreshing && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing customer messages…
          </div>
        )}

        {error && (
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={() => setError(null)}
              className="rounded-xl p-1.5 text-rose-600 transition-colors hover:bg-rose-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5 xl:gap-4">
          <StatCard
            label="Total"
            value={summary?.total || 0}
            sub="All messages"
            icon={<Send className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Delivered"
            value={summary?.delivered || 0}
            sub="Successful messages"
            icon={<CheckCircle2 className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Failed"
            value={summary?.failed || 0}
            sub="Unsuccessful messages"
            icon={<XCircle className="h-5 w-5" />}
            tone={(summary?.failed || 0) > 0 ? "rose" : "slate"}
          />

          <StatCard
            label="SMS"
            value={summary?.sms || 0}
            sub="Text messages"
            icon={<Smartphone className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="WhatsApp"
            value={summary?.whatsapp || 0}
            sub="WhatsApp messages"
            icon={<MessageCircle className="h-5 w-5" />}
            tone="emerald"
          />
        </div>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[420px_1fr]">
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
            <div className="border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5">
              <p className="text-[14px] font-black text-slate-900">
                Customer Details
              </p>

              <p className="text-[11.5px] font-medium text-slate-500">
                Basic customer and service information.
              </p>
            </div>

            <div className="space-y-3 p-4 sm:p-5">
              <DetailRow
                icon={<User className="h-4 w-4" />}
                label="Name"
                value={customerName}
              />

              <DetailRow
                icon={<Phone className="h-4 w-4" />}
                label="Phone"
                value={customer?.phone_number || "—"}
              />

              <DetailRow
                icon={<Mail className="h-4 w-4" />}
                label="Email"
                value={customer?.email || "—"}
              />

              <DetailRow
                icon={<Wallet className="h-4 w-4" />}
                label="Account"
                value={customer?.account_number || "—"}
              />

              <DetailRow
                icon={<Wifi className="h-4 w-4" />}
                label="Plan"
                value={customer?.plan_name || "—"}
              />

              <DetailRow
                icon={<Router className="h-4 w-4" />}
                label="Router"
                value={customer?.router_name || "—"}
              />

              <DetailRow
                icon={<Clock className="h-4 w-4" />}
                label="Service Expiry"
                value={formatDateTime(customer?.service_expires_at)}
              />

              <div className="rounded-3xl border border-slate-200 bg-slate-100 px-4 py-4">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  Latest Message
                </p>

                <p className="mt-2 text-[12.5px] font-bold text-slate-800">
                  {latestMessage
                    ? formatDateTime(
                        latestMessage.sent_at || latestMessage.created_at,
                      )
                    : "No message yet"}
                </p>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
            <div className="border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5">
              <p className="text-[14px] font-black text-slate-900">
                Message Details
              </p>

              <p className="text-[11.5px] font-medium text-slate-500">
                Message body, delivery status, provider ID, cost and time.
              </p>
            </div>

            <div className="divide-y divide-slate-200 bg-slate-50">
              {messages.length > 0 ? (
                messages.map((msg) => (
                  <div key={msg.id} className="px-4 py-4 sm:px-5">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="flex flex-wrap items-center gap-2">
                        <ChannelBadge channel={msg.channel} />
                        <StatusBadge
                          delivered={msg.delivered}
                          status={msg.status}
                        />

                        <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                          <Clock className="h-3 w-3" />
                          {formatDateTime(msg.sent_at || msg.created_at)}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => copyText(msg.message || "")}
                        className={`${btnBase} h-9 border border-slate-300 bg-slate-50 px-3 text-slate-600 hover:bg-slate-100 hover:text-slate-800`}
                      >
                        <Copy className="h-3.5 w-3.5" />
                        Copy
                      </button>
                    </div>

                    <div className="mt-3 rounded-3xl border border-slate-200 bg-slate-100 px-4 py-4">
                      <p className="whitespace-pre-wrap text-[13px] font-medium leading-relaxed text-slate-800">
                        {msg.message || "—"}
                      </p>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                      <MiniInfo label="Phone" value={msg.phone_number || "—"} />
                      <MiniInfo
                        label="Provider ID"
                        value={msg.provider_message_id || "—"}
                      />
                      <MiniInfo
                        label="Cost"
                        value={formatMoney(msg.cost, msg.currency)}
                      />
                      <MiniInfo
                        label="Created"
                        value={formatDateTime(msg.created_at)}
                      />
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-4 py-16 text-center">
                  <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                    <MessageCircle className="h-7 w-7 text-slate-500" />
                  </div>

                  <p className="text-[14px] font-black text-slate-800">
                    No messages found
                  </p>

                  <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                    This customer does not have any recorded messages yet.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerMessagesDetailPage;
